import classNames from "classnames";
import { setTrackingId } from "client/Misc/utils";
import {
	Context,
	createContext,
	MouseEvent,
	ReactElement,
	ReactNode,
	useContext,
	useMemo,
	useState
} from "react";
import { MediaQueryKind, useWorkspaceMediaQuery } from "../../Hooks";
import { BusyIndicator } from "../BusyIndicator/BusyIndicator";
import { DropOverContext } from "../DropOver/DropOver";
import { ResponsiveMenu } from "../ResponsiveMenu/ResponsiveMenu";
import styles from "./ActionListBox.module.less";

export type ActionListBoxProps = {
	isHeightConstrainted?: boolean;
	width?: number;
	responsiveMode?: {
		kind: "auto" | "never";
		height?: "fill";
	};
	hasBusyIndicator?: boolean;
	menuTitle: string;
	onMouseDown?: (args: MouseEvent) => void;
	trackingId: string | undefined;
	children: ReactNode;
};

export const ActionListBox = (props: ActionListBoxProps): ReactElement => {
	const {
		isHeightConstrainted = false,
		responsiveMode = { kind: "auto" },
		hasBusyIndicator = false,
		width,
		menuTitle,
		onMouseDown,
		trackingId,
		children
	} = props;

	const [collapsibleSectionCount, setCollapsibleSectionCount] = useState<number>(0);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const {
		onClose
	} = useContext(DropOverContext);

	const actionListBoxContextData = useMemo(
		(): ActionListBoxContextData => ({
			registerCollapsibleSection: (): (() => void) => {
				setCollapsibleSectionCount(x => x + 1);
				return () => setCollapsibleSectionCount(x => x - 1);
			}
		}),
		[]
	);

	if (responsiveMode.kind !== "never" && isResponsive) {
		return (
			<ResponsiveMenu
				onClose={onClose}
				trackingId={trackingId}
				height={
					responsiveMode.height === "fill" || collapsibleSectionCount > 0
						? "fill"
						: undefined
				}
				hasBusyIndicator={hasBusyIndicator}
				onMouseDown={onMouseDown}
			>
				<ResponsiveMenu.Header title={menuTitle} />

				<ResponsiveMenu.Body goesEdgeToEdge>
					<ActionListBoxContext.Provider value={actionListBoxContextData}>
						{children}
					</ActionListBoxContext.Provider>
				</ResponsiveMenu.Body>
			</ResponsiveMenu>
		);
	}

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{
					[styles.root_withBusyIndicator]: hasBusyIndicator
				}
			)}
			style={{ width }}
			onMouseDown={onMouseDown}
		>
			<div
				className={classNames(
					styles.scrollable,
					{
						[styles.scrollable_heightConstrained]: isHeightConstrainted
					}
				)}
			>
				{children}
			</div>
			{hasBusyIndicator && <BusyIndicator />}
		</div>
	);
};
ActionListBox.displayName = "ActionListBox";

export type ActionListBoxContextData = {
	registerCollapsibleSection?(): () => void;
};

export const ActionListBoxContext: Context<ActionListBoxContextData> = createContext<ActionListBoxContextData>(
	{
		registerCollapsibleSection: undefined
	}
);
